import { WebCitation, SourceCardImage, SourceCardImageIdentity } from '@digital-twins/shared-types'

import { httpsImageSrc, sourceImageTokens } from '@/components/citations/citationSource'

import { PartitionedEvidenceV2 } from './partitionAssistantCitations'

/** The part of a rendered turn that carries web citations. */
export interface SourcePreviewTurn {
  webCitations?: Array<WebCitation>
  citationV2?: PartitionedEvidenceV2
  cardImages?: Array<SourceCardImage>
  sourceImagesLoading?: boolean
}

/** Page url to its https preview image, for one message. */
export type SourcePreviewMap = ReadonlyMap<string, string>

export interface SourcePreviewData {
  loading?: boolean
  images: SourcePreviewMap
  cardImages?: Array<SourceCardImage>
}

/** Literal rich-card identity, never a title similarity or meaning guess. */
export function sourceCardImageKey(card: Omit<SourceCardImageIdentity, 'cite'> & { cite?: Array<string> }): string {
  return JSON.stringify([card.title, card.body, card.cite ?? [], card.image ?? null])
}

/** Only the scoped application image route or existing HTTPS image URLs may render. */
export function sourceCardImageSrc(value: unknown): string | null {
  return typeof value === 'string' && sourceImageTokens(value) ? value : httpsImageSrc(value)
}

function imageOf(value: unknown): string | null {
  return httpsImageSrc((value as { imageUrl?: unknown } | null | undefined)?.imageUrl)
}

/**
 * The web page urls of a turn that still show no image: the web source cards
 * and the v2 web citation targets. Empty when every web citation already has one.
 */
export function webUrlsWithoutImage(turn: SourcePreviewTurn): Array<string> {
  const urls: Array<string> = []
  const add = (url: unknown, image: string | null) => {
    if (typeof url !== 'string' || image) {
      return
    }
    const trimmed = url.trim()
    if (trimmed.startsWith('https://') && !urls.includes(trimmed)) {
      urls.push(trimmed)
    }
  }
  for (const citation of turn.webCitations ?? []) {
    add(citation.url, imageOf(citation))
  }
  for (const target of turn.citationV2?.targets ?? []) {
    if (target?.kind === 'web') {
      add(target.citation.url, imageOf(target.citation))
    }
  }
  return urls
}

/**
 * The turn with each preview image merged onto the web citations that lack one,
 * so `ThreadWebSourceCard` and `citationImage` read it like any other image.
 * Returns the same turn object when nothing changes.
 */
export function withSourcePreviews<T extends SourcePreviewTurn>(
  turn: T,
  previews: SourcePreviewData | undefined,
): T & SourcePreviewTurn {
  if (!previews || (previews.images.size === 0 && previews.cardImages === undefined && previews.loading === undefined)) {
    return turn
  }
  const previewFor = (url: unknown, current: unknown): string | null => {
    if (typeof url !== 'string' || imageOf(current)) {
      return null
    }
    return httpsImageSrc(previews.images.get(url.trim()))
  }

  let changed = (previews.cardImages !== undefined && previews.cardImages !== turn.cardImages)
    || (previews.loading !== undefined && previews.loading !== turn.sourceImagesLoading)
  const webCitations = turn.webCitations?.map((citation) => {
    const imageUrl = previewFor(citation.url, citation)
    if (!imageUrl) {
      return citation
    }
    changed = true
    return { ...citation, imageUrl }
  })
  const targets = turn.citationV2?.targets.map((target) => {
    if (target?.kind !== 'web') {
      return target
    }
    const imageUrl = previewFor(target.citation.url, target.citation)
    if (!imageUrl) {
      return target
    }
    changed = true
    return { ...target, citation: { ...target.citation, imageUrl } }
  })
  if (!changed) {
    return turn
  }
  return {
    ...turn,
    ...(previews.loading !== undefined ? { sourceImagesLoading: previews.loading } : {}),
    ...(previews.cardImages !== undefined ? { cardImages: previews.cardImages } : {}),
    ...(webCitations ? { webCitations } : {}),
    ...(turn.citationV2 && targets ? { citationV2: { ...turn.citationV2, targets } } : {}),
  }
}
